import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type FC,
} from "react";
import {
  useGlobalModalContext,
  useGlobalModalOpen,
  type ModalProps,
} from "../../context/GlobalModal";
import type { V2PrivacyControls } from "../../privacy";
import { Button } from "../buttons/Button";
import { usePrivateModeRecoveryModal } from "./PrivateModeToggleV2";
import "../../scss/private_mode_access.scss";

type Snapshot = {
  controls: V2PrivacyControls;
  available: boolean;
  scope: string;
};
const shownThisRuntime = new Set<string>();
const bindingFor = (snapshot: Snapshot) =>
  JSON.stringify([snapshot.scope, snapshot.controls.activeAccountId]);
const eligible = (snapshot?: Snapshot) =>
  !!snapshot &&
  snapshot.available &&
  !!snapshot.scope &&
  !!snapshot.controls.activeAccountId &&
  snapshot.controls.backupRecommended === true &&
  snapshot.controls.status === "ready" &&
  !snapshot.controls.privateSessionLocked &&
  !snapshot.controls.sessionExpired &&
  !snapshot.controls.recoveryExportRequired;

// Persist only a domain-separated opaque identity marker, never wallet data.
const storageKey = async (binding: string): Promise<string | undefined> => {
  try {
    const digest = await globalThis.crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(`redacted.backup-reminder.v1:${binding}`)
    );
    return (
      "redacted:v2:backup-reminder:" +
      [...new Uint8Array(digest)]
        .map((value) => value.toString(16).padStart(2, "0"))
        .join("")
    );
  } catch {
    return undefined;
  }
};
const wasShown = (binding: string, key?: string) => {
  if (shownThisRuntime.has(binding)) return true;
  try {
    return !!key && localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};
const rememberShown = (binding: string, key?: string) => {
  shownThisRuntime.add(binding);
  try {
    if (key) localStorage.setItem(key, "1");
  } catch {
    /* Login must work without browser storage. */
  }
};
const createStore = (initial: Snapshot) => {
  let current: Snapshot | undefined = initial;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => current,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    update: (next?: Snapshot) => {
      current = next;
      listeners.forEach((listener) => listener());
    },
  };
};

const Reminder: FC<{
  store: ReturnType<typeof createStore>;
  binding: string;
  dismiss: () => void;
  openBackup: () => void;
}> = ({ store, binding, dismiss, openBackup }) => {
  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
  const current =
    eligible(snapshot) && !!snapshot && bindingFor(snapshot) === binding;
  useEffect(() => {
    if (!current) dismiss();
  }, [current, dismiss]);
  return (
    <section
      className="private-mode-access"
      role="dialog"
      aria-modal="true"
      aria-labelledby="private-backup-reminder-title"
      aria-describedby="private-backup-reminder-description">
      <div className="private-mode-access__content">
        <h2 id="private-backup-reminder-title">Back up your private account</h2>
        <p
          id="private-backup-reminder-description"
          className="private-mode-access__description">
          Keep access if you lose this wallet app or move to a different wallet
          app.
        </p>
        <Button
          type="button"
          className="button--primary"
          disabled={!current}
          onClick={openBackup}
          label="Back up now"
        />
        <button
          type="button"
          className="transparent private-mode-access__link"
          onClick={dismiss}>
          Later
        </button>
      </div>
    </section>
  );
};

/** Mount once outside account-data Suspense, like the expiry notification. */
export const PrivateModeBackupReminder: FC<{
  controls: V2PrivacyControls;
  available?: boolean;
  scope: string;
}> = ({ controls, available = true, scope }) => {
  const snapshot = useMemo(
    () => ({ controls, available, scope }),
    [controls, available, scope]
  );
  const [store] = useState(() => createStore(snapshot));
  useLayoutEffect(() => store.update(snapshot), [snapshot, store]);
  useLayoutEffect(() => () => store.update(undefined), [store]);
  const { showModalIfClosed, isCurrentModal, hideModal } =
    useGlobalModalContext();
  const modalOpen = useGlobalModalOpen();
  const open = usePrivateModeRecoveryModal(controls);
  const openRef = useRef(open);
  useLayoutEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (modalOpen || !eligible(snapshot)) return;
    const binding = bindingFor(snapshot);
    if (shownThisRuntime.has(binding)) return;
    let cancelled = false;
    void (async () => {
      const key = await storageKey(binding);
      const current = store.getSnapshot();
      if (
        cancelled ||
        !eligible(current) ||
        !current ||
        bindingFor(current) !== binding ||
        wasShown(binding, key)
      )
        return;
      let modal: ModalProps;
      const dismiss = () => {
        if (isCurrentModal(modal)) hideModal();
      };
      const openBackup = () => {
        const live = store.getSnapshot();
        if (
          !isCurrentModal(modal) ||
          !eligible(live) ||
          !live ||
          bindingFor(live) !== binding
        )
          return;
        openRef.current("export", true);
      };
      modal = {
        title: "",
        backgroundClose: false,
        hideCloseButton: true,
        // Reminder closes itself once its private account changes.
        keepOnAccountChange: true,
        className: "modal--unlock-private",
        children: (
          <Reminder
            store={store}
            binding={binding}
            dismiss={dismiss}
            openBackup={openBackup}
          />
        ),
      };
      if (showModalIfClosed(modal)) rememberShown(binding, key);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    snapshot,
    store,
    modalOpen,
    showModalIfClosed,
    isCurrentModal,
    hideModal,
  ]);
  return null;
};
